/**
 * StaffAndRoles — the single entry point that replaces the split between
 * People, Staff and Settings › Team & roles.
 *
 * Operator decisions this implements are recorded in
 * `docs/staff-and-roles-spec.md`. The two that shape the code most:
 *
 *  • **Roles are per LOCATION, and there is no ranking.** A person holds a
 *    `(location, role)` row for each location they work at. This screen RECORDS
 *    those; it does not compute an "effective role" and must not display one —
 *    no rule exists that would make it true. `can()` keeps resolving exactly as
 *    it does today. That gap is deliberate and documented, not an oversight.
 *  • **Sign-in and bookable are independent.** Either, both, or neither. A
 *    bookable person with no login is a real state and is shown as one.
 *
 * Nothing is retired in this pass: People, Staff and Settings › Team & roles
 * all stay reachable by URL until this screen is signed off.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AddRowButton, Chip, ChipToggleGroup, ConfirmDialog, Dropdown, IconBtn, IconTile, InlineEmptyState, PageState, Table, type TableColumn, alpha, Avatar, Banner, Btn, Card, Checkbox, Field, Icon, Input, Pill, Section, SectionLabel, StatusPill, SummaryList, SummaryRow, T, Tab, Tabs } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useIsMobile } from "../../shell/useResponsive";
import { FilterBar, FilterDropdown } from "../../shell/composites";
import { useRowSelection, BulkActionBar, type BulkAction } from "../../shell/list-composites";
import { toast } from "../../shell/toast";
import {
  can, useUser, PERM_CATALOG, PROCESSES, type ProcessId,
  listRoles, getRoleDef,
  inviteMemberOp, resendInviteOp, setMemberRoleOp, removeMemberOp, refetchMembers,
  listApiRoles, getApiRole, type ApiRole, type Member,
  effectivePerms, startImpersonation,
} from "../../shell/access";
import { useT, t as i18nT, formatICU } from "../../lib/i18n";
import { friendlyDateTime, getDateTimeConfig, toDate, useDateTimeVersion } from "../../lib/datetime";
import { useNav } from "../../shell/router";
import { logAction } from "../../lib/audit";
import { usePeople, type Person } from "./usePeople";
import { StaffAvailabilityTab, ComingSoonTab } from "./StaffAvailabilityTab";
import { StaffAbsencesTab } from "./StaffAbsencesTab";
import { StaffCalendarTab } from "./StaffCalendarTab";
import {
  useTeamRoles, createTeamRoleOp, updateTeamRoleOp, deleteTeamRoleOp, teamRoleErrorMessage, type TeamRole,
} from "../../lib/team-roles-api";
import { useStaff, type StaffMember } from "../staff/useStaff";
import { WelcomeEmailButton } from "../staff/WelcomeEmailButton";
import { SvcSectionHead } from "../../lib/svc-editor-primitives";
import { GHOST_CLASS, GHOST_RADIUS, GhostStyles, SkeletonBox } from "../../lib/loading-skeletons";

/* 2026-09-10 (operator): Staff and Roles are TWO routes now — `people` (this
   screen, the roster) and `roles` (RolesScreen at the bottom of this file,
   System-Administrator-only). Neither carries a tab strip: one surface per
   route. The Groups tab is retired — group management never moved off
   `staff.groups`, so the tab only ever announced a future move. */

/* ── Shared derivations — used by the roster AND the person page ─────────── */

/** The status pill, defined once so the roster row, the person page's identity
 *  card and its Access & status block can never disagree. */
function statusPillOf(p: Person) {
  if (p.status === "invited") return <Pill tone="warn">{i18nT("staffRoles.invited1", "Invited")}</Pill>;
  if (p.status === "on-leave") return <Pill tone="neutral">{i18nT("staffRoles.onLeave", "On leave")}</Pill>;
  if (p.status === "inactive") return <Pill tone="neutral" dot>{i18nT("staffRoles.inactive", "Inactive")}</Pill>;
  return <Pill tone="success" dot>{i18nT("staffRoles.active", "Active")}</Pill>;
}

/* Last login (2026-10-08). The team API serves `lastActiveAt` as a raw ISO
   instant and the roster printed it verbatim. It is rendered here in the
   VIEWER's display zone, the one lib/datetime resolves from their own
   preference (then their profile, the workspace, the browser), never the
   person's or a location's zone, with that zone named in parentheses. A value
   that is not an ISO instant is a server-formatted label and is shown as served. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
function viewerZoneName(at: Date): string {
  const { locale, timeZone } = getDateTimeConfig();
  try {
    return new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "long" })
      .formatToParts(at).find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}
/** The roster's Last login cell and the person page's Last login row, defined
 *  once so the two can never disagree. */
function lastLoginOf(p: Person, member: Member | undefined): string {
  if (!p.memberId) return i18nT("staffRoles.neverSignedIn", "No login");
  /* No member row to read (the roster read failed or has not landed): say the
     value is unknown rather than claim they never signed in. */
  if (!member) return i18nT("staffRoles.lastLoginUnavailable", "Not available");
  const raw = (member.last ?? "").trim();
  if (!raw || raw === "—") {
    return p.status === "invited"
      ? i18nT("staffRoles.lastLoginPendingInvite", "Not signed in yet")
      : i18nT("staffRoles.lastLoginNever", "Never signed in");
  }
  const at = ISO_INSTANT.test(raw) ? toDate(raw) : null;
  if (!at) return raw;
  const zone = viewerZoneName(at);
  return zone ? `${friendlyDateTime(at)} (${zone})` : friendlyDateTime(at);
}

/* The per-location role is a PROCESS role — the same catalogue the Roles screen
   edits ("Personal Banker", "Branch Manager"), NOT the four workspace roles
   from listApiRoles(). Operator, 2026-08-08: "the full capabilities of the role
   setup applied on the people tab". Prefixed by process so two roles sharing a
   name across processes stay distinguishable.
   The roles an admin can assign are the ones this VERTICAL actually uses —
   derived from the tenant's own personas (seed-world/<tenant>/personas.json),
   which is the same source the sign-in chooser lists. Offering the full
   ROLE_CATALOG across all five processes showed a bank tenant's admins Store Ops and
   Appointments roles nobody in that tenant holds (operator, 2026-08-08).
   Falls back to the events catalogue only when the roster serves no
   assignments at all, so the picker is never empty. */
function rolesFromPeople(people: Person[]): { key: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const person of people) {
    for (const pr of PROCESSES) {
      const roleId = (person.assignments ?? {})[pr.id];
      if (!roleId || seen.has(roleId)) continue;
      const d = getRoleDef(roleId, pr.id);
      seen.set(roleId, pr.label + " · " + (d?.label ?? roleId));
    }
  }
  if (!seen.size) {
    for (const r of listRoles("events" as ProcessId)) seen.set(r.id, r.label);
  }
  /* Roles already recorded AGAINST A LOCATION count too — otherwise a person
     whose only role is a per-location one would open to a picker that cannot
     show what they currently hold. */
  for (const person of people) {
    for (const lr of person.locationRoles) {
      if (!lr.roleKey || seen.has(lr.roleKey)) continue;
      let label = lr.roleKey;
      for (const pr of PROCESSES) {
        const d = getRoleDef(lr.roleKey, pr.id);
        if (d) { label = pr.label + " · " + d.label; break; }
      }
      seen.set(lr.roleKey, label);
    }
    const dk = person.defaultRoleKey;
    if (dk && !seen.has(dk)) {
      let label = dk;
      for (const pr of PROCESSES) {
        const d = getRoleDef(dk, pr.id);
        if (d) { label = pr.label + " · " + d.label; break; }
      }
      seen.set(dk, label);
    }
  }
  return [...seen.entries()].map(([key, label]) => ({ key, label }));
}

/** Only the signed-in tenant Owner can grant or change workspace access. The
 * verified session role is independent of the paginated team roster. Missing
 * role or failed member data fails closed instead of granting controls. */
function useCanManageWorkspaceRoles(membersUnavailable: boolean): boolean {
  const user = useUser();
  return !membersUnavailable && user.workspaceRole?.trim().toLowerCase() === "owner";
}

/* ── Per-location role rows — the heart of the Add form and the record ───── */

export interface LocRole { locationId: string; roleKey: string; home?: boolean }

/* ── Workspace roles (ported from Settings › Team & roles, retired 2026-08-10) ──
 * TWO role vocabularies meet on this screen, deliberately unmapped:
 *   • WORKSPACE roles — what the team API administers (owner/admin/member…).
 *     Invites and membership writes carry these.
 *   • PROCESS roles — shell/access.ts's capability model `can()` resolves
 *     against, recorded per person and per location and edited on the Roles tab.
 * No equivalence is invented between them. */
function WorkspaceRolePicker({ roles, value, onChange }: { roles: ApiRole[]; value: string; onChange: (key: string) => void }) {
  const t = useT();
  return (
    <ChipToggleGroup
      ariaLabel={t("people.staffAndRoles.ariaLabel", "Role")}
      value={value}
      onChange={(v) => onChange(v as string)}
      options={roles.map((r) => ({ value: r.key, label: r.label }))}
    />
  );
}

/* Capability labels (2026-10-08). The backend builds `permissions[i]` as the
   catalog LABEL KEY for `capabilities[i]` ("shell.access.viewAuditLog"); it was
   printed raw. Keys go through the i18n catalog, falling back to a label read
   off the capability id itself, never to the key. */
const LABEL_KEY = /^[A-Za-z][\w-]*(\.[\w-]+)+$/;
const ACCESS_VERB: Record<string, [string, string]> = {
  read: ["staffRoles.capVerbView", "View"], view: ["staffRoles.capVerbView", "View"], list: ["staffRoles.capVerbView", "View"],
  write: ["staffRoles.capVerbManage", "Manage"], manage: ["staffRoles.capVerbManage", "Manage"],
  edit: ["staffRoles.capVerbManage", "Manage"], update: ["staffRoles.capVerbManage", "Manage"],
};
const ACCESS_LEVEL: Record<string, [string, string]> = {
  read: ["staffRoles.capLevelRead", "read access"], view: ["staffRoles.capLevelRead", "read access"], list: ["staffRoles.capLevelRead", "read access"],
  write: ["staffRoles.capLevelWrite", "write access"], manage: ["staffRoles.capLevelWrite", "write access"],
  edit: ["staffRoles.capLevelWrite", "write access"], update: ["staffRoles.capLevelWrite", "write access"],
};
const words = (s: string) => s.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ").trim().toLowerCase();
const capAction = (capId: string) => (capId.includes(".") ? capId.slice(capId.lastIndexOf(".") + 1) : "");
/** "audit.read" → "View audit"; "identity.team.manage" → "Manage identity team". */
function capabilityFallbackLabel(capId: string): string {
  const action = capAction(capId);
  const subject = words(action ? capId.slice(0, capId.lastIndexOf(".")).replace(/\./g, " ") : capId);
  const verb = ACCESS_VERB[action.toLowerCase()];
  const head = verb ? i18nT(verb[0], verb[1]) : words(action);
  const text = head ? `${head} ${subject}` : subject;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
function capabilityLabel(capId: string, labelKey: string | undefined): string {
  const fallback = capabilityFallbackLabel(capId);
  if (!labelKey || labelKey === capId) return fallback;
  return LABEL_KEY.test(labelKey) ? i18nT(labelKey, fallback) : labelKey;
}
/** What distinguishes one capability from another that shares its label —
 *  `audit.read` and `audit.write` both carry viewAuditLog today. */
function capabilityLevel(capId: string): string {
  const action = capAction(capId);
  const level = ACCESS_LEVEL[action.toLowerCase()];
  return level ? i18nT(level[0], level[1]) : words(action) || capId;
}
/** The read-only list, one entry per distinct capability and per distinct
 *  visible label. Capabilities that resolve to the SAME label are not merged
 *  into one claim: each keeps its own entry, told apart by access level. */
function readOnlyCapabilityItems(role: ApiRole): { key: string; label: string }[] {
  const firstIndex = new Map<string, number>();
  role.capabilities.forEach((capId, i) => { if (!firstIndex.has(capId)) firstIndex.set(capId, i); });
  const byLabel = new Map<string, string[]>();
  firstIndex.forEach((i, capId) => {
    const label = capabilityLabel(capId, role.permissions[i]);
    byLabel.set(label, [...(byLabel.get(label) ?? []), capId]);
  });
  const items: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  byLabel.forEach((ids, label) => {
    for (const capId of ids) {
      let text = ids.length > 1 ? `${label} (${capabilityLevel(capId)})` : label;
      if (seen.has(text)) text = `${label} (${capabilityFallbackLabel(capId)})`;
      seen.add(text);
      items.push({ key: capId, label: text });
    }
  });
  return items;
}

/** Capability rows for a WORKSPACE role. Editable only on invite, where the API
 *  accepts a NARROWED set; it rejects capabilities above the role. Read-only
 *  everywhere else, where it is a static list (2026-10-08): it used to be a
 *  two-column form of disabled, checked boxes, which reads as a control that
 *  is merely switched off. */
function ApiCapabilityList({ role, selected, onChange }: { role: ApiRole | undefined; selected?: Set<string>; onChange?: (next: Set<string>) => void }) {
  const t = useT();
  if (!role) return null;
  if (!onChange) {
    const items = readOnlyCapabilityItems(role);
    if (!items.length) {
      return <div style={{ fontSize: 12, color: T.soft }}>{t("staffRoles.noCapabilitiesInRole", "This role grants no capabilities.")}</div>;
    }
    return (
      <ul aria-label={t("staffRoles.capabilitiesInThisRole", "Capabilities in this role")} data-testid="role-capabilities-readonly"
        style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: 6 }}>
        {items.map((it) => (
          <li key={it.key} title={it.key} style={{ minWidth: 0, maxWidth: "100%" }}>
            <StatusPill tone="neutral" style={{ whiteSpace: "normal", overflowWrap: "anywhere", maxWidth: "100%" }}>{it.label}</StatusPill>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px" }}>
      {[...new Set(role.capabilities)].map((capId) => {
        const i = role.capabilities.indexOf(capId);
        const checked = !!selected?.has(capId);
        return (
          <label key={capId} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "4px 0" }}>
            <Checkbox checked={checked} onChange={(on) => {
              const next = new Set(selected ?? []);
              if (on) next.add(capId); else next.delete(capId);
              onChange(next);
            }} />
            <span style={{ fontSize: 12, color: checked ? T.n1 : T.soft, textDecoration: checked ? "none" : "line-through" }}>{capabilityLabel(capId, role.permissions[i])}</span>
          </label>
        );
      })}
    </div>
  );
}

function LocationRoleRows({ rows, locations, roles, defaultRole, onChange, disabled }: {
  rows: LocRole[];
  locations: { id: string; name: string }[];
  roles: { key: string; label: string }[];
  /** The person's default role. A row with no `roleKey` inherits it — that is
   *  what an empty value MEANS here, not "unset" (operator, 2026-08-08). */
  defaultRole: { key: string; label: string } | null;
  onChange: (r: LocRole[]) => void;
  disabled?: boolean;
}) {
  const t = useT();
  const set = (i: number, patch: Partial<LocRole>) => onChange(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));
  /* 2026-09-11 (KP, design-system review): these rows were hand-rolled —
     native <select>s with a local `sel` style object, and an IconBtn (a fixed
     square, icon-only control) asked to carry the text "Home", which overflowed
     its 36px box and collided with the remove button. Every control here is now
     the design system's: Dropdown for the two pickers, Chip for the home
     toggle (it is a two-state selection, which is what a chip is for),
     AddRowButton for the append affordance. IconBtn keeps only the icon-only
     remove, which is what it is for. No behaviour change. */
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Dropdown size="sm" disabled={disabled} value={r.locationId || null}
            onChange={(v) => set(i, { locationId: v })}
            placeholder={i18nT("staffRoles.chooseLocation", "Choose a location…")}
            aria-label={i18nT("staffRoles.location", "Location")}
            style={{ flex: 1.15, minWidth: 0 }}
            options={locations.map((l) => ({ value: l.id, label: l.name }))} />
          {/* Placeholder reads just "Same as default" — NOT the role name. The
              default is named in full in its own picker directly above this
              list, and a collapsed trigger here is ~150px: spelling it out per
              row read "Same as default (Workf…" and clipped the very word it
              was trying to show. */}
          <Dropdown size="sm" disabled={disabled} value={r.roleKey || null}
            onChange={(v) => set(i, { roleKey: v })}
            placeholder={defaultRole
              ? i18nT("staffRoles.sameAsDefault", "Same as default")
              : i18nT("staffRoles.chooseRole", "Choose a role…")}
            aria-label={i18nT("staffRoles.roleAtThisLocation", "Role at this location")}
            style={{ flex: 1.45, minWidth: 0 }}
            options={roles.map((x) => ({ value: x.key, label: x.label }))} />
          <Chip selected={!!r.home} disabled={disabled} leading={<Icon.loc size={12} />}
            onClick={() => onChange(rows.map((x, n) => ({ ...x, home: n === i })))}
            title={t("staffRoles.markHome", "Mark as home location")}>
            {t("staffRoles.home", "Home")}
          </Chip>
          <IconBtn size={32} type="button" disabled={disabled} onClick={() => onChange(rows.filter((_, n) => n !== i))} title={t("common.actions.remove", "Remove")}>
            <Icon.x size={14} />
          </IconBtn>
        </div>
      ))}
      <AddRowButton full={false} disabled={disabled}
        onClick={() => onChange([...rows, { locationId: "", roleKey: "", home: rows.length === 0 }])}>
        {t("staffRoles.addLocation", "Add a location")}
      </AddRowButton>
      <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.45 }}>
        {t("staffRoles.perLocationNote", "Every location uses the default role unless you set a different one on that row. Roles are not ranked — holding two doesn't combine them.")}
      </div>
    </div>
  );
}

/* ── Add staff — one form, everything at once ────────────────────────────── */

function AddStaffForm({ locations, roles, lobs, canManageWorkspaceRoles, onClose, onDone }: {
  locations: { id: string; name: string }[];
  roles: { key: string; label: string }[];
  lobs: string[];
  canManageWorkspaceRoles: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const { upsertMember } = useStaff();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [defaultRoleKey, setDefaultRoleKey] = useState("");
  const [rows, setRows] = useState<LocRole[]>([{ locationId: "", roleKey: "", home: true }]);
  const [lob, setLob] = useState<string[]>([]);
  const [signsIn, setSignsIn] = useState(true);
  const [bookable, setBookable] = useState(true);
  /* Workspace role + optional capability narrowing (ported from the retired
     Settings › Team & roles invite modal). Only meaningful when the person
     signs in — a bookable-only record has no membership to carry it. */
  const apiRoles = listApiRoles();
  const [wsRoleKey, setWsRoleKey] = useState("");
  const wsRole = getApiRole(canManageWorkspaceRoles ? (wsRoleKey || "member") : "member");
  const [narrowed, setNarrowed] = useState(false);
  const [caps, setCaps] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = i18nT("staffRoles.nameRequired", "Enter a name.");
    if (signsIn && !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email.trim())) e.email = i18nT("staffRoles.emailRequired", "A valid email is required for anyone who can sign in.");
    if (!defaultRoleKey) e.role = i18nT("staffRoles.defaultRoleRequired", "Choose the role they hold by default.");
    if (signsIn && !wsRole) e.workspaceRole = i18nT("staffRoles.workspaceRoleUnavailable", "Workspace roles are still loading. Please retry.");
    if (!rows.some((r) => r.locationId)) e.rows = i18nT("staffRoles.locationRequired", "Assign at least one location.");
    return e;
  };

  const save = async () => {
    const e = validate();
    setErr(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    const filled = rows.filter((r) => r.locationId);
    const parts = name.trim().split(/\s+/);
    const now = new Date().toISOString();
    try {
      /* The ROSTER RECORD is the person, and it is created for real. This form
         used to toast "Staff member added" for a non-signing-in person without
         calling a single op (no-fake-interactivity). Locations and the role at
         each ride in the create payload, so the record lands complete instead
         of needing a second write. */
      await upsertMember({
        id: "", staffId: "",
        firstName: parts[0] ?? "", lastName: parts.slice(1).join(" "),
        name: name.trim(), email: email.trim(), phone: "", title: title.trim(),
        bio: "", photo: "", photoUrl: null, color: null,
        status: "active", signsIn, role: defaultRoleKey,
        channels: bookable ? ["appointments"] : [],
        groups: [], serviceGrants: [], serviceExclusions: [],
        skills: [], languages: [], department: null, category: null, tags: [],
        locations: filled.map((r) => ({ locationId: r.locationId, roleKey: r.roleKey || null, home: !!r.home })),
        concurrency: 1, dailyCap: 0, timeOff: [],
        slug: "", linkEnabled: false, createdAt: now, updatedAt: now,
      } as StaffMember);
      /* Sign-in is a separate, MEMBERSHIP fact: only invite when the switch is
         on. The role it carries is the person's DEFAULT — the membership holds
         one role, and the default is precisely the role that isn't tied to a
         single location. */
      if (signsIn) {
        await inviteMemberOp({
          displayName: name.trim(), email: email.trim(),
          roleKey: wsRole!.key,
          capabilities: canManageWorkspaceRoles && narrowed ? [...caps] : undefined,
        });
        logAction("staff", email.trim(), name.trim(), "invited");
      } else {
        logAction("staff", name.trim(), name.trim(), "created");
      }
      toast({
        tone: "success",
        title: signsIn ? t("staffRoles.invited", "Invitation sent") : t("staffRoles.staffAdded", "Staff member added"),
        subtitle: name.trim(),
      });
      onDone();
      onClose();
    } catch (x) {
      toast({ tone: "danger", title: t("staffRoles.couldnTAdd", "Couldn't add this person"), subtitle: x instanceof Error ? x.message : String(x) });
    } finally {
      setSaving(false);
    }
  };

  const label = (s: string, req?: boolean) => (
    <div style={{ fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 5 }}>{s}{req ? <span style={{ color: T.red1 }}> *</span> : null}</div>
  );

  return (
    <Card style={{ padding: 20, marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.n1 }}>{t("staffRoles.addAStaffMember", "Add a staff member")}</h3>
        <span style={{ flex: 1 }} />
        <IconBtn size={32} onClick={onClose}><Icon.x size={16} /></IconBtn>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>{label(t("staffRoles.fullName", "Full name"), true)}
          <Input value={name} onChange={(e) => setName(e.target.value)} error={err.name} placeholder={t("people.staffAndRoles.placeholder", "Marcus Thompson")} /></div>
        <div style={{ flex: 1, minWidth: 200 }}>{label(t("staffRoles.email", "Email"), signsIn)}
          <Input value={email} onChange={(e) => setEmail(e.target.value)} error={err.email} placeholder={t("people.staffAndRoles.placeholder2", "m.thompson@example.com")} /></div>
        <div style={{ flex: 1, minWidth: 180 }}>{label(t("staffRoles.jobTitle", "Job title"))}
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("people.staffAndRoles.placeholder3", "Personal banker")} /></div>
      </div>

      <div style={{ marginBottom: 14, maxWidth: 420 }}>
        {label(t("staffRoles.defaultRole", "Default role"), true)}
        <Dropdown value={defaultRoleKey || null} onChange={(v) => setDefaultRoleKey(v)}
          placeholder={i18nT("staffRoles.chooseRole", "Choose a role…")}
          aria-label={t("staffRoles.defaultRole", "Default role")}
          error={err.role || undefined}
          helperText={err.role ? undefined : t("staffRoles.defaultRoleNote", "Applied at every location they work at. A location can be given a different role below.")}
          options={roles.map((r) => ({ value: r.key, label: r.label }))} />
      </div>

      <div style={{ marginBottom: 14 }}>
        {label(t("staffRoles.locationsAndRoles", "Locations & role at each"), true)}
        <LocationRoleRows rows={rows} locations={locations} roles={roles} onChange={setRows}
          defaultRole={defaultRoleKey ? { key: defaultRoleKey, label: roles.find((r) => r.key === defaultRoleKey)?.label ?? defaultRoleKey } : null} />
        {err.rows ? <div style={{ marginTop: 6, fontSize: 12, color: T.red1 }}>{err.rows}</div> : null}
      </div>

      <div style={{ marginBottom: 14 }}>
        {label(t("staffRoles.linesOfBusiness", "Lines of business"))}
        {!lobs.length ? (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 12px", borderRadius: 8, background: T.canvas, border: `1px dashed ${T.n4}` }}>
            <span style={{ color: T.soft, flexShrink: 0, marginTop: 1 }}><Icon.info size={14} /></span>
            <span style={{ fontSize: 12, color: T.n2, lineHeight: 1.45 }}>
              {t("staffRoles.lobsNotServed", "Lines of business aren't served to this screen yet — they live in the tenant's services catalog. Assigning one needs listServiceLobs; declared in docs/staff-and-roles-spec.md.")}
            </span>
          </div>
        ) : null}
      </div>
      {lobs.length ? (
        <div style={{ marginBottom: 14 }}>
          <ChipToggleGroup
            multiple
            ariaLabel={t("people.staffAndRoles.ariaLabel2", "Lines of business")}
            values={lob}
            onChange={(next) => setLob(next as string[])}
            options={lobs.map((l) => ({ value: l, label: l }))}
          />
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", padding: "12px 16px", borderRadius: 8, background: T.canvas, marginBottom: 16 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <Checkbox checked={signsIn} onChange={() => setSignsIn(!signsIn)} />
          <span><span style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("staffRoles.canSignIn", "Can sign in")}</span>
            <span style={{ display: "block", fontSize: 12, color: T.soft }}>{t("staffRoles.sendsAnInvite", "Sends an invitation to their email")}</span></span>
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <Checkbox checked={bookable} onChange={() => setBookable(!bookable)} />
          <span><span style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("staffRoles.canBeBooked", "Can be booked")}</span>
            <span style={{ display: "block", fontSize: 12, color: T.soft }}>{t("staffRoles.appearsInAvailability", "Appears in availability and the booking flow")}</span></span>
        </label>
      </div>

      {signsIn ? (
        <div style={{ marginBottom: 16 }}>
          {label(t("staffRoles.workspaceRole", "Workspace role"), true)}
          {apiRoles.length && wsRole ? (
            <>
              {canManageWorkspaceRoles ? (
                <WorkspaceRolePicker roles={apiRoles} value={wsRole.key} onChange={setWsRoleKey} />
              ) : (
                <Chip selected effect="inert">{wsRole.label}</Chip>
              )}
              <div style={{ marginTop: 6, fontSize: 12, color: T.soft, lineHeight: 1.45 }}>
                {wsRole?.description || t("staffRoles.workspaceRoleNote", "What their sign-in grants at workspace level. Separate from the default role above, which is the process role capability checks resolve against.")}
              </div>
              {canManageWorkspaceRoles ? <Btn size="sm" variant="link" icon={<Icon.chev size={12} dir={narrowed ? "up" : "down"} />}
                onClick={() => { const next = !narrowed; setNarrowed(next); if (next && wsRole) setCaps(new Set(wsRole.capabilities)); }}>
                {narrowed ? t("staffRoles.hideCapabilities", "Hide capabilities") : t("staffRoles.narrowCapabilities", "Narrow the capabilities of this invite")}
              </Btn> : null}
              {canManageWorkspaceRoles && narrowed ? (
                <div style={{ marginTop: 10, padding: "12px 16px", background: T.n6, borderRadius: 8 }}>
                  <ApiCapabilityList role={wsRole} selected={caps} onChange={setCaps} />
                </div>
              ) : null}
            </>
          ) : (
            <div style={{ fontSize: 12, color: T.soft, padding: "8px 12px", background: T.n6, borderRadius: 8 }}>
              {t("staffRoles.workspaceRoleUnavailable", "Workspace roles are still loading. Please retry.")}
            </div>
          )}
          {err.workspaceRole ? <div style={{ marginTop: 6, fontSize: 12, color: T.red1 }}>{err.workspaceRole}</div> : null}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
        <Btn icon={<Icon.check size={14} />} disabled={saving} onClick={() => void save()}>
          {saving ? t("staffRoles.saving", "Saving…") : t("staffRoles.addStaffMember", "Add staff member")}
        </Btn>
      </div>
    </Card>
  );
}

/* ── Roles — the tenant's role definitions, on the identity team API ────── */

/** What a role grants, labelled from the server's own label keys: the backend
 *  builds `permissions[i]` as the catalog label key for `capabilities[i]`. */
function RoleCapabilityList({ role, checked, disabled, onToggle }: {
  role: TeamRole | undefined;
  checked: string[];
  disabled?: boolean;
  onToggle?: (capability: string) => void;
}) {
  if (!role) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
      {role.capabilities.map((capability, i) => (
        <label key={capability} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.n1, cursor: disabled ? "default" : "pointer" }}>
          <Checkbox checked={checked.includes(capability)} disabled={disabled} onChange={() => onToggle?.(capability)} />
          <span style={{ minWidth: 0 }}>{i18nT(role.permissions[i] ?? capability, capability)}</span>
        </label>
      ))}
    </div>
  );
}

interface RoleDraft { name: string; basedOn: string; capabilities: string[] }

function RolesTab({ roles }: { roles: TeamRole[] }) {
  const t = useT();
  /* Reading is the route's gate (`team.read` = identity.team.read). Every write
     needs `team.write`, the shell's projection of identity.team.manage — and a
     viewer without it gets no write controls at all, not disabled ones. */
  const canManage = can("team.write");
  const builtIns = roles.filter((r) => !r.isCustom);
  const [selected, setSelected] = useState<string>(roles[0]?.key ?? "");
  const [creating, setCreating] = useState(false);
  /* Edits are staged and committed by Save, so an accidental click isn't an
     immediate change for everyone holding the role. */
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const [busy, setBusy] = useState<"save" | "create" | "delete" | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  /* The backend's own refusal (name taken, role still assigned, capability
     outside the base role), shown where the change was attempted. */
  const [problem, setProblem] = useState<string | null>(null);

  const role = creating ? undefined : roles.find((r) => r.key === selected) ?? roles[0];
  const baseOf = (key: string | null | undefined) => builtIns.find((r) => r.key === key);
  /* A custom role can only hold capabilities of its built-in base, so the base
     is the list it is edited against. A built-in role lists its own. */
  const base = creating ? baseOf(draft?.basedOn) : role?.isCustom ? baseOf(role.basedOn) ?? role : role;
  const editing = (): RoleDraft | null =>
    draft ?? (role ? { name: role.label, basedOn: role.basedOn ?? "", capabilities: role.capabilities } : null);
  const checked = draft?.capabilities ?? role?.capabilities ?? [];
  const dirty = !!role && !!draft && (draft.name.trim() !== role.label
    || draft.capabilities.length !== role.capabilities.length
    || draft.capabilities.some((c) => !role.capabilities.includes(c)));

  const pick = (key: string) => { setSelected(key); setCreating(false); setDraft(null); setProblem(null); };
  const startCreate = (basedOn: string = builtIns[0]?.key ?? "") => {
    const from = baseOf(basedOn);
    setCreating(true);
    setProblem(null);
    setDraft({ name: "", basedOn, capabilities: from ? [...from.capabilities] : [] });
  };
  const toggle = (capability: string) => {
    const current = editing();
    if (!canManage || !current) return;
    setDraft({
      ...current,
      capabilities: current.capabilities.includes(capability)
        ? current.capabilities.filter((c) => c !== capability)
        : [...current.capabilities, capability],
    });
  };
  const rename = (name: string) => {
    const current = editing();
    if (current) setDraft({ ...current, name });
  };

  const create = async () => {
    if (!draft) return;
    setBusy("create");
    setProblem(null);
    try {
      const result = await createTeamRoleOp({ name: draft.name.trim(), basedOn: draft.basedOn, permissions: draft.capabilities });
      /* Stale = the tenant changed while this was in flight. It is not a
         refusal and it is not this tenant's success: say nothing. */
      if (result.status === "stale") return;
      setCreating(false);
      setDraft(null);
      if (result.role) setSelected(result.role.key);
      toast({ tone: "success", title: t("staffRoles.roleCreated", "Role created"), subtitle: result.role?.label ?? draft.name.trim() });
    } catch (error) {
      setProblem(teamRoleErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  const save = async () => {
    if (!role || !draft || !dirty) return;
    setBusy("save");
    setProblem(null);
    try {
      const result = await updateTeamRoleOp(role.key, { name: draft.name.trim(), permissions: draft.capabilities });
      if (result.status === "stale") return;
      setDraft(null);
      toast({ tone: "success", title: t("staffRoles.roleSaved", "Role saved"), subtitle: draft.name.trim() });
    } catch (error) {
      setProblem(teamRoleErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };
  const remove = async () => {
    if (!role) return;
    setBusy("delete");
    setProblem(null);
    try {
      const result = await deleteTeamRoleOp(role.key);
      if (result.status === "stale") return;
      setConfirmingDelete(false);
      setDraft(null);
      setSelected(builtIns[0]?.key ?? "");
      toast({ tone: "success", title: t("staffRoles.roleDeleted", "Role deleted"), subtitle: role.label });
    } catch (error) {
      setConfirmingDelete(false);
      setProblem(teamRoleErrorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const roleHue = (i: number) =>
    /* Illustrative role hues — the tertiary ramp, which is what the token set
       reserves for chart/illustration colour. */
    [T.tPurple, T.tOrange, T.tTurquoise, T.tGreen, T.tDarkBlue, T.tPink, T.tRed][i % 7];
  const draftValid = !!draft && !!draft.name.trim() && draft.capabilities.length > 0;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 700, color: T.n1 }}>{t("staffRoles.manageRoles", "Manage roles")}</h3>
        <p style={{ margin: 0, fontSize: 12, color: T.soft, lineHeight: 1.5, maxWidth: 720 }}>
          {t("staffRoles.manageTeamRolesBlurb", "Built-in roles come with the workspace and can't be changed. A custom role starts from a built-in role and can only narrow what it grants.")}
        </p>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Card style={{ width: 232, flexShrink: 0, padding: 8 }}>
          {roles.map((r, i) => {
            const on = !creating && r.key === role?.key;
            return (
              /* ui-raw: full-width clickable list row (avatar + two lines) — no row-as-button component exists */
              <button key={r.key} type="button" onClick={() => pick(r.key)}
                style={{ display: "flex", gap: 8, width: "100%", padding: "8px 12px", borderRadius: 8, border: "none", background: on ? T.p4 : "transparent", cursor: "pointer", textAlign: "left", fontFamily: T.font, alignItems: "flex-start" }}>
                <span style={{ width: 12, height: 12, borderRadius: 4, background: roleHue(i), flexShrink: 0, marginTop: 4 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: on ? 700 : 600, color: on ? T.p1 : T.n1 }}>{r.label}</span>
                  <span style={{ display: "block", fontSize: 12, color: r.memberCount ? T.soft : T.orange1, marginTop: 2 }}>
                    {r.memberCount ? r.memberCount : t("staffRoles.noHolders", "No holders")} · {r.capabilities.length} {t("staffRoles.caps", "Caps")}
                    {r.isCustom ? <> · {t("staffRoles.customRole", "Custom")}</> : null}
                  </span>
                </span>
              </button>
            );
          })}
          {canManage ? (
            <div style={{ marginTop: 4 }}>
              <AddRowButton disabled={busy !== null || builtIns.length === 0} onClick={() => startCreate()}>
                {t("staffRoles.newRole", "New role")}
              </AddRowButton>
            </div>
          ) : null}
        </Card>

        <Card style={{ flex: 1, minWidth: 340, padding: 16 }}>
          {problem ? (
            <Banner tone="danger" size="compact" style={{ marginBottom: 12 }}>{problem}</Banner>
          ) : null}
          {creating && draft ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <Pill tone="primary">{t("staffRoles.newRole", "New role")}</Pill>
                <span style={{ fontSize: 13, color: T.n2 }}>{draft.capabilities.length} {t("staffRoles.capabilities", "capabilities")}</span>
                <span style={{ flex: 1 }} />
                <Btn size="sm" variant="link" disabled={busy !== null} onClick={() => { setCreating(false); setDraft(null); setProblem(null); }}>
                  {t("common.actions.cancel", "Cancel")}
                </Btn>
                <Btn size="sm" disabled={busy !== null || !draftValid || !draft.basedOn} onClick={() => void create()}>
                  {busy === "create" ? t("staffRoles.saving", "Saving…") : t("staffRoles.createRole", "Create role")}
                </Btn>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 16 }}>
                <Field label={t("staffRoles.roleName", "Role name")}>
                  <Input value={draft.name} maxLength={80} disabled={busy !== null} onChange={(e) => rename(e.target.value)} />
                </Field>
                <Field label={t("staffRoles.basedOn", "Based on")}>
                  <Dropdown value={draft.basedOn || null} disabled={busy !== null}
                    aria-label={t("staffRoles.basedOn", "Based on")}
                    onChange={(v) => { const from = baseOf(v); setDraft({ ...draft, basedOn: v, capabilities: from ? [...from.capabilities] : [] }); }}
                    options={builtIns.map((r) => ({ value: r.key, label: r.label }))} />
                </Field>
              </div>
              <SectionLabel style={{ marginBottom: 8 }}>{t("staffRoles.whatItGrants", "What it grants")}</SectionLabel>
              <RoleCapabilityList role={base} checked={draft.capabilities} disabled={busy !== null} onToggle={toggle} />
            </>
          ) : !role ? (
            <InlineEmptyState size="sm" icon={<IconTile icon={<Icon.lock size={16} />} />}
              title={t("staffRoles.pickARole", "Pick a role")}
              description={t("staffRoles.chooseARoleToSee", "Choose a role to see what it grants.")} />
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <Pill tone="primary">{role.label}</Pill>
                <span style={{ fontSize: 13, color: T.n2 }}>{checked.length} {t("staffRoles.capabilities", "capabilities")}</span>
                <span style={{ flex: 1 }} />
                {canManage && role.isCustom ? (
                  <>
                    <Btn size="sm" variant="danger" disabled={busy !== null} onClick={() => setConfirmingDelete(true)}>
                      {t("staffRoles.deleteRole", "Delete role")}
                    </Btn>
                    <Btn size="sm" disabled={busy !== null || !dirty || !draftValid} onClick={() => void save()}>
                      {busy === "save" ? t("staffRoles.saving", "Saving…") : t("staffRoles.saveChanges", "Save changes")}
                    </Btn>
                  </>
                ) : null}
                {canManage && !role.isCustom ? (
                  <Btn size="sm" variant="secondary" disabled={busy !== null} onClick={() => startCreate(role.key)}>
                    {t("staffRoles.createFromThisRole", "Create a custom role from this")}
                  </Btn>
                ) : null}
              </div>
              {role.description ? (
                <p style={{ margin: "0 0 16px", fontSize: 13, color: T.n2, lineHeight: 1.5 }}>{role.description}</p>
              ) : null}
              {role.isCustom ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16, maxWidth: 420 }}>
                  {canManage ? (
                    <Field label={t("staffRoles.roleName", "Role name")}>
                      <Input value={draft?.name ?? role.label} maxLength={80} disabled={busy !== null} onChange={(e) => rename(e.target.value)} />
                    </Field>
                  ) : null}
                  <span style={{ fontSize: 12, color: T.soft }}>
                    {t("staffRoles.basedOnRole", "Based on")} {baseOf(role.basedOn)?.label ?? role.basedOn}
                  </span>
                </div>
              ) : (
                <Banner tone="info" size="compact" style={{ marginBottom: 16 }}>
                  {t("staffRoles.builtInReadOnly", "Built-in roles can't be changed.")}
                </Banner>
              )}
              <SectionLabel style={{ marginBottom: 8 }}>{t("staffRoles.whatItGrants", "What it grants")}</SectionLabel>
              <RoleCapabilityList role={base} checked={checked}
                disabled={!canManage || !role.isCustom || busy !== null} onToggle={toggle} />
              <ConfirmDialog open={confirmingDelete} tone="danger" busy={busy === "delete"}
                title={t("staffRoles.deleteRoleTitle", "Delete this role?")}
                consequence={t("staffRoles.deleteRoleConsequence", "The role is removed for the whole workspace. A role someone still holds can't be deleted — reassign them first.")}
                confirmLabel={t("staffRoles.deleteRole", "Delete role")}
                cancelLabel={t("common.actions.cancel", "Cancel")}
                onCancel={() => setConfirmingDelete(false)} onConfirm={() => void remove()} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ── Person record — the edit path for someone who already exists ─────────
 * 2026-09-11 (operator): this is a PAGE now, not a drawer. A roster row
 * navigates to `people.person` and this renders one TAB of that page; the
 * identity header and the tab strip belong to PersonPage below. Real ops run
 * for real; anything with no op behind it is DISABLED and names what it waits
 * on, rather than looking editable and silently discarding the change. */
export type PersonTab = "personal" | "roles" | "availability" | "absences" | "calendar" | "meeting";
const PERSON_TABS: PersonTab[] = ["personal", "roles", "availability", "absences", "calendar", "meeting"];

/* Person ghost (CC, 2026-10-08): the section head and the first two cards of a
   person's record, in grey while the roster read is pending. The page header and
   the section rail are real chrome and already render. PageState owns
   role/aria-busy and the announcement; these shapes are decorative. */
// ui-config — ghost shape: field cells in the first card, rows in the second
const PERSON_GHOST_FIELDS = 4;
const PERSON_GHOST_ROWS = 3;
function PersonDetailGhost() {
  return (
    <div className={GHOST_CLASS} style={{ width: "100%", maxWidth: 880, display: "flex", flexDirection: "column", gap: 16 }}>
      <GhostStyles />
      <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 4 }}>
        <SkeletonBox width={38} height={38} radius={10} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          <SkeletonBox variant="text" width={140} height={18} />
          <SkeletonBox variant="text" width="60%" height={13} />
        </div>
      </div>
      <Section>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
            <SkeletonBox variant="text" width={160} height={16} />
            <SkeletonBox variant="text" width="55%" height={13} />
          </div>
          <SkeletonBox width={64} height={32} radius={GHOST_RADIUS.control} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 16 }}>
          {Array.from({ length: PERSON_GHOST_FIELDS }, (_, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <SkeletonBox variant="text" width={90} height={12} />
              <SkeletonBox height={40} radius={GHOST_RADIUS.control} />
            </div>
          ))}
        </div>
      </Section>
      <Section>
        <SkeletonBox variant="text" width={140} height={16} />
        <div style={{ display: "flex", flexDirection: "column", marginTop: 10 }}>
          {Array.from({ length: PERSON_GHOST_ROWS }, (_, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 0", borderTop: `1px solid ${T.border}` }}>
              <SkeletonBox variant="text" width={140} height={13} />
              <SkeletonBox variant="text" width={180} height={13} />
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function PersonRecord({ person, tab, staffRecord, locations, roles, canManageWorkspaceRoles, lastLogin, statusPill, onClose, onChanged }: {
  person: Person;
  /** Which tab's content to render — `personal` or `roles`; the two empty
   *  tabs are drawn by PersonPage, which has nothing to pass them yet. */
  tab: PersonTab;
  /** The person's ROSTER record, when they have one — the only record name,
   *  job title and email can be written back to. */
  staffRecord: StaffMember | undefined;
  locations: { id: string; name: string }[];
  roles: { key: string; label: string }[];
  canManageWorkspaceRoles: boolean;
  /** Same value the roster's Last login column renders. */
  lastLogin: string;
  /** The roster's own status pill, passed in so the two can never diverge. */
  statusPill: (p: Person) => React.ReactNode;
  onClose: () => void;
  onChanged: () => void;
}) {
  const t = useT();
  const nav = useNav();
  const { setLocationRoles, setDefaultRole, upsertMember } = useStaff();
  const canWrite = can("team.write");
  /* What this person can ACTUALLY do — role + per-process assignments + any
     per-person grants/revokes, resolved by the same function the shell uses.
     Read-only here: narrowing an individual writes grants/revokes, and no op
     is exported for that yet. */
  const caps = useMemo(() => {
    /* effectivePerms wants a PROCESS role (Event Manager, Personal Banker) —
       person.roleKey is the WORKSPACE role (owner/admin/member) and resolves to
       nothing. SettingsTeam passes `processRole || role`; the equivalent here is
       the person's own per-process assignments, with the events assignment as
       the single-role fallback for rows that predate them. */
    const a = person.assignments ?? {};
    const primary = a.events ?? Object.values(a).find(Boolean) ?? person.roleKey;
    return effectivePerms({ role: primary, assignments: a });
  }, [person]);
  /* The rows ARE the person's assignments — read from the record, written back
     whole (setStaffLocationRoles replaces the set). `stamp` is what Save
     compares against, so the button is live only when something really moved. */
  const rowsOf = (src: Person): LocRole[] =>
    src.locationRoles.length
      ? src.locationRoles.map((r) => ({ locationId: r.locationId, roleKey: r.roleKey, home: r.home }))
      : [{ locationId: "", roleKey: "", home: true }];
  const stamp = (r: LocRole[]) => JSON.stringify(r.filter((x) => x.locationId).map((x) => [x.locationId, x.roleKey, !!x.home]));
  const [rows, setRows] = useState<LocRole[]>(() => rowsOf(person));
  const [savedStamp, setSavedStamp] = useState(() => stamp(rowsOf(person)));
  const [rowsErr, setRowsErr] = useState("");
  const [roleKey, setRoleKey] = useState(person.defaultRoleKey || "");
  /* Basic information — the four identity fields the roster record actually
     carries. Nothing else from a staff profile is invented here: no ID, no
     description, no booking name, because no field behind them is served. */
  const [first, setFirst] = useState(staffRecord?.firstName ?? person.name.split(/\s+/)[0] ?? "");
  const [last, setLast] = useState(staffRecord?.lastName ?? person.name.split(/\s+/).slice(1).join(" "));
  const [jobTitle, setJobTitle] = useState(person.title || "");
  const [email, setEmail] = useState(person.email || "");
  const basicsDirty = !!staffRecord && (
    first !== (staffRecord.firstName ?? "") || last !== (staffRecord.lastName ?? "") ||
    jobTitle !== (staffRecord.title ?? "") || email !== (staffRecord.email ?? "")
  );
  /* Workspace role (owner/admin/member…) — the membership vocabulary, ported
     from the retired Settings › Team & roles Edit-access modal. */
  const wsRoles = listApiRoles();
  const [wsKey, setWsKey] = useState(person.roleKey || "");
  const wsDirty = !!wsKey && wsKey !== person.roleKey;
  const [busy, setBusy] = useState<string | null>(null);
  const locsDirty = stamp(rows) !== savedStamp;
  /* An invitation nobody has accepted yet is withdrawn, not "removed"
     (2026-10-08). The served op is the same membership revoke — there is no
     dedicated cancel-invitation op — but the words and the confirmation say what
     it does to an invite. Errors stay in the dialog so a retry keeps context. */
  const invited = person.status === "invited" && !!person.memberId;
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const revokeInvitation = async () => {
    if (!person.memberId || busy) return;
    setBusy("revoke");
    setRevokeError(null);
    try {
      await removeMemberOp(person.memberId);
      logAction("staff", person.memberId, person.name, "invitation revoked");
      /* Read back: re-read the team roster so the record reflects what the
         server now holds, not only the local removal. */
      void refetchMembers();
      onChanged();
      setConfirmingRevoke(false);
      toast({
        tone: "success",
        title: t("staffRoles.invitationRevoked", "Invitation revoked"),
        subtitle: person.staffId
          ? formatICU(t("staffRoles.invitationRevokedKeepsRoster", "{name} can no longer accept it. Their roster record is unchanged."), { name: person.name })
          : formatICU(t("staffRoles.invitationRevokedBody", "{name} can no longer accept it."), { name: person.name }),
      });
      onClose();
    } catch (e) {
      setRevokeError(e instanceof Error && e.message ? e.message : t("staffRoles.retryInAMoment", "Retry in a moment."));
    } finally {
      setBusy(null);
    }
  };

  const run = async (id: string, fn: () => Promise<unknown>, ok: string, verb?: string) => {
    if (busy) return;
    setBusy(id);
    try {
      await fn();
      /* Logged only after the op resolves — an entry for a failed write would
         be worse than none. */
      logAction("staff", person.memberId ?? person.key, person.name, verb ?? "updated");
      onChanged();
      toast({ tone: "success", title: ok, subtitle: person.name });
    }
    catch (e) { toast({ tone: "danger", title: t("staffRoles.couldnTSave", "Couldn't save"), subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(null); }
  };

  const sectionHead = (s: string) => <SectionLabel as="h4" style={{ margin: "16px 0 8px" }}>{s}</SectionLabel>;

  /* What the inheriting rows below inherit FROM. It follows the picker, not the
     stored value: the two are saved by different buttons, and rows describing a
     default the admin has already changed would be describing something that is
     no longer on screen. The pending state is announced by a pill on the
     section header — not inside the row's <select>, where it was clipped away
     ("Same as default (Workf…") and never actually read. */
  const labelFor = (k: string) => roles.find((r) => r.key === k)?.label ?? k;
  const effectiveDefault = roleKey || person.defaultRoleKey;
  const rowsDefault = effectiveDefault ? { key: effectiveDefault, label: labelFor(effectiveDefault) } : null;
  const defaultUnsaved = !!roleKey && roleKey !== person.defaultRoleKey;

  /* Per-location roles live on the ROSTER record: that is where locations are
     stored, and it is the only id a bookable person with no workspace
     membership has. Someone with a membership but no roster record therefore
     has nothing to write to — the editor says so instead of pretending. */
  const saveLocations = () => {
    const filled = rows.filter((r) => r.locationId);
    setRowsErr("");
    void run("locs", async () => {
      await setLocationRoles(person.staffId as string, filled.map((r) => ({ locationId: r.locationId, roleKey: r.roleKey, home: !!r.home })));
      setRows(filled.length ? filled : [{ locationId: "", roleKey: "", home: true }]);
      setSavedStamp(stamp(filled));
    }, t("staffRoles.locationsSaved", "Locations & roles saved"), "locations updated");
  };

  /* Identity writes go to the roster record whole — upsertMember is the only
     served write for these fields, and it takes the full member. */
  const saveBasics = () => {
    if (!staffRecord) return;
    void run("basics", async () => {
      await upsertMember({
        ...staffRecord,
        firstName: first.trim(), lastName: last.trim(),
        name: [first.trim(), last.trim()].filter(Boolean).join(" "),
        title: jobTitle.trim(), email: email.trim(),
      } as StaffMember);
    }, t("staffRoles.detailsSaved", "Details saved"), "details updated");
  };

  return (
    <div style={{ width: "100%", maxWidth: 880, display: "flex", flexDirection: "column", gap: 16 }}>
      {tab === "personal" ? (
        <>
        {/* Basic information — DS Section + Field + Input (2026-09-11 review:
            the first pass hand-rolled the card, the labels and the header). */}
        <Section
          title={t("staffRoles.basicInformation", "Basic information")}
          description={t("staffRoles.basicInformationBlurb", "What this person is called, and where we reach them.")}
          actions={
            <Btn size="sm" disabled={!canWrite || !staffRecord || !basicsDirty || busy !== null} onClick={saveBasics}>
              {busy === "basics" ? t("staffRoles.saving", "Saving…") : t("common.actions.save", "Save")}
            </Btn>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 16 }}>
            <Field label={t("staffRoles.firstName", "First name")}>
              <Input value={first} disabled={!canWrite || !staffRecord} onChange={(e) => setFirst(e.target.value)} />
            </Field>
            <Field label={t("staffRoles.lastName", "Last name")}>
              <Input value={last} disabled={!canWrite || !staffRecord} onChange={(e) => setLast(e.target.value)} />
            </Field>
            <Field label={t("staffRoles.jobTitle", "Job title")}>
              <Input value={jobTitle} disabled={!canWrite || !staffRecord} onChange={(e) => setJobTitle(e.target.value)} placeholder={t("people.staffAndRoles.placeholder3", "Personal banker")} />
            </Field>
            <Field label={t("staffRoles.email", "Email")}>
              <Input value={email} disabled={!canWrite || !staffRecord} onChange={(e) => setEmail(e.target.value)} />
            </Field>
          </div>
          {!staffRecord ? (
            <Banner tone="warning" size="compact" style={{ marginTop: 12 }}
              title={t("staffRoles.noRosterIdentity", "Name, job title and email are stored on the roster record, and this person doesn't have one — they hold a workspace membership only. Add them to the staff roster to edit these.")} />
          ) : null}
        </Section>

        {/* Access & status — the roster's own columns, repeated verbatim.
            Opening a person must not show them anything different from the row
            they clicked (operator, 2026-08-08), so these read from the SAME
            values the table renders, including its own status pill. Rendered
            as the DS SummaryList, which is what a key/value review block is. */}
        <Section title={t("staffRoles.accessAndStatus", "Access & status")}>
          <SummaryList style={{ marginTop: 10 }}>
            <SummaryRow label={t("staffRoles.roleAccess", "Role / access")}>
              {person.roleLabel || roles.find((r) => r.key === person.defaultRoleKey)?.label || person.defaultRoleKey || "—"}
            </SummaryRow>
            <SummaryRow label={t("staffRoles.signInAndBooking", "Sign-in & booking")}>
              {[person.signsIn ? t("staffRoles.signsIn", "Signs in") : null,
                person.bookable ? t("staffRoles.bookable", "Bookable") : null]
                .filter(Boolean).join(" · ") || t("staffRoles.recordOnly", "Record only")}
            </SummaryRow>
            <SummaryRow label={t("staffRoles.location", "Location")}>
              {person.locations.length ? person.locations.map((l) => l.name).join(", ") : "—"}
            </SummaryRow>
            <SummaryRow label={t("staffRoles.status", "Status")}>{statusPill(person)}</SummaryRow>
            <SummaryRow label={t("staffRoles.lastLogin", "Last login")}>{lastLogin}</SummaryRow>
          </SummaryList>
        </Section>
        </>
      ) : null}

      {tab === "roles" ? (
        <div>
          {sectionHead(t("staffRoles.defaultRole", "Default role"))}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Dropdown value={roleKey || null} disabled={!canWrite || (!person.staffId && !person.memberId)}
              onChange={(v) => setRoleKey(v)}
              placeholder={i18nT("staffRoles.chooseRole", "Choose a role…")}
              aria-label={t("staffRoles.defaultRole", "Default role")}
              style={{ flex: 1, minWidth: 200 }}
              options={roles.map((r) => ({ value: r.key, label: r.label }))} />
            <Btn size="sm" disabled={!canWrite || (!person.staffId && !person.memberId) || !roleKey || roleKey === person.defaultRoleKey || busy !== null}
              onClick={() => void run("role", async () => {
                /* The default lives on the ROSTER record — the only record a
                   person without a membership has. The WORKSPACE role is a
                   different vocabulary and is saved by its own section below
                   (the two were conflated before Team & roles merged in here). */
                if (person.staffId) await setDefaultRole(person.staffId, roleKey);
              }, t("staffRoles.roleUpdated", "Default role updated"), "role changed")}>
              {busy === "role" ? t("staffRoles.saving", "Saving…") : t("common.actions.save", "Save")}
            </Btn>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: T.soft, lineHeight: 1.45 }}>
            {t("staffRoles.defaultRoleNote", "Applied at every location they work at. A location can be given a different role below.")}
          </div>
          {!person.memberId ? (
            <div style={{ marginTop: 6, fontSize: 12, color: T.soft }}>
              {t("staffRoles.noMembership", "This person has no workspace membership — they can be booked but cannot sign in.")}
            </div>
          ) : null}

          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "18px 0 8px" }}>
            <SectionLabel as="h4" style={{ margin: 0 }}>{t("staffRoles.locationsAndRoles", "Locations & role at each")}</SectionLabel>
            {defaultUnsaved ? <Pill tone="warn">{t("staffRoles.newDefaultUnsaved", "New default not saved")}</Pill> : null}
            <span style={{ flex: 1 }} />
            <Btn size="sm" disabled={!canWrite || !person.staffId || !locsDirty || busy !== null} onClick={saveLocations}>
              {busy === "locs" ? t("staffRoles.saving", "Saving…") : t("common.actions.save", "Save")}
            </Btn>
          </div>
          <LocationRoleRows rows={rows} locations={locations} roles={roles} onChange={setRows} disabled={!canWrite || !person.staffId}
            defaultRole={rowsDefault} />
          {rowsErr ? <div style={{ marginTop: 6, fontSize: 12, color: T.red1 }}>{rowsErr}</div> : null}
          {!person.staffId ? (
            <div style={{ marginTop: 10 }}>
              <Banner tone="info" size="compact">
                {t("staffRoles.noRosterRecord", "Where someone works is stored on their roster record, and this person doesn't have one — they hold a workspace membership only. Add them to the staff roster to record locations and a role at each.")}
              </Banner>
            </div>
          ) : null}

          {person.memberId ? (
            <>
              {sectionHead(t("staffRoles.workspaceRole", "Workspace role"))}
              {wsRoles.length ? (
                <>
                  {canManageWorkspaceRoles ? <WorkspaceRolePicker roles={wsRoles} value={wsKey} onChange={setWsKey} /> : <Chip selected effect="inert">{getApiRole(person.roleKey)?.label ?? person.roleKey}</Chip>}
                  <div style={{ marginTop: 6, fontSize: 12, color: T.soft, lineHeight: 1.45 }}>
                    {getApiRole(wsKey)?.description || t("staffRoles.workspaceRoleNote", "What their sign-in grants at workspace level. Separate from the default role above, which is the process role capability checks resolve against.")}
                  </div>
                  {canManageWorkspaceRoles ? <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                    <Btn size="sm" disabled={!wsDirty || busy !== null}
                      onClick={() => void run("wsrole", () => setMemberRoleOp(person.memberId as string, wsKey), t("staffRoles.accessUpdated", "Workspace access updated"), "workspace role changed")}>
                      {busy === "wsrole" ? t("staffRoles.saving", "Saving…") : t("staffRoles.saveAccess", "Save access")}
                    </Btn>
                    <span style={{ fontSize: 12, color: T.soft }}>
                      {t("staffRoles.perPersonOverridesPending", "Per-person capability overrides aren't available yet — no declared op (REQ-074).")}
                    </span>
                  </div> : null}
                  {getApiRole(wsKey) ? (
                    <div style={{ marginTop: 12, padding: "8px 12px", background: alpha(T.p1, "0a"), border: `1px solid ${T.p2}`, borderRadius: 8 }}>
                      <SectionLabel style={{ marginBottom: 8 }}>{t("staffRoles.capabilitiesInThisRole", "Capabilities in this role")}</SectionLabel>
                      <ApiCapabilityList role={getApiRole(wsKey)} />
                    </div>
                  ) : null}
                </>
              ) : (
                <div style={{ fontSize: 12, color: T.soft, padding: "8px 12px", background: T.n6, borderRadius: 8 }}>
                  {t("staffRoles.workspaceRolesNotLoaded2", "Workspace roles aren't loaded yet — reopen once the roster has finished loading.")}
                </div>
              )}
            </>
          ) : null}

          {sectionHead(t("staffRoles.whatTheyCanDo", "What they can do"))}
          {caps.size ? (
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: T.n1 }}>{caps.size}</span>
              <span style={{ fontSize: 12, color: T.soft }}>{t("staffRoles.capabilitiesResolved", "capabilities, resolved from their role and assignments")}</span>
            </div>
          ) : (
            /* No process-role assignment on this row, so there is nothing to
               resolve. Saying "0 capabilities" would be a claim about their
               access; this says what is actually true. */
            <Banner tone="info" size="compact">
              {t("staffRoles.noProcessRole", "No capabilities can be resolved for this person: capabilities come from a process role (Event Manager, Personal Banker) and this record carries only a workspace role. Their per-location roles are recorded, but by design they don't feed access resolution yet — see docs/staff-and-roles-spec.md.")}
            </Banner>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 240, overflow: "auto", padding: "8px 12px", borderRadius: 8, background: T.canvas }}>
            {PERM_CATALOG.map((group) => {
              const held = group.caps.filter((c) => caps.has(c.id));
              if (!held.length) return null;
              return (
                <div key={group.id}>
                  <SectionLabel style={{ marginBottom: 4 }}>{group.label}</SectionLabel>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {held.map((c) => (
                      <Chip key={c.id} effect="inert">{c.label}</Chip>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: T.soft, lineHeight: 1.45 }}>
            {t("staffRoles.narrowingNeedsOp", "Narrowing an individual's capabilities writes per-person grants/revokes — no op is exported for that yet. Editing the ROLE, in the Roles tab, changes it for everyone who holds it.")}
          </div>

        </div>
      ) : null}

      {tab === "personal" ? (
        <div>
          {sectionHead(t("staffRoles.actions", "Actions"))}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Btn size="sm" variant="secondary" disabled={busy !== null || (!person.roleKey && !person.defaultRoleKey && !Object.keys(person.assignments ?? {}).length)}
              title={(!person.roleKey && !person.defaultRoleKey && !Object.keys(person.assignments ?? {}).length)
                ? i18nT("staffRoles.cantActAsNoRole", "No role is recorded for this person, so there is nothing to act as yet.")
                : undefined}
              onClick={() => {
                /* Acting-as, not becoming: the banner in the shell names the
                   admin the whole time and the audit trail attributes what
                   they do to THEM, on this person's behalf. Nothing is logged
                   or navigated unless the swap actually took — it used to
                   toast and navigate even when startImpersonation refused. */
                const started = startImpersonation({
                  id: person.memberId ?? person.staffId ?? undefined,
                  name: person.name, email: person.email,
                  role: person.roleKey || person.defaultRoleKey,
                  init: person.init, color: person.color, assignments: person.assignments,
                });
                if (!started) {
                  toast({ tone: "danger", title: t("staffRoles.cantActAs", "Can't act as this person"), subtitle: i18nT("staffRoles.cantActAsNoRole", "No role is recorded for this person, so there is nothing to act as yet.") });
                  return;
                }
                logAction("staff", person.memberId ?? person.key, person.name, "started acting as");
                toast({ tone: "info", title: i18nT("staffRoles.nowActingAs", "You're now acting as ") + person.name, subtitle: t("staffRoles.leaveFromBanner", "Leave from the banner at the top of the screen.") });
                nav.go("dashboard");
              }}>
              {t("staffRoles.viewAsThisPerson", "View as this person")}
            </Btn>
            <Btn size="sm" variant="secondary" disabled={!person.memberId || busy !== null}
              onClick={() => void run("invite", () => resendInviteOp(person.memberId as string), t("staffRoles.inviteResent", "Invitation resent"), "invite resent")}>
              {t("staffRoles.resendInvite", "Resend invitation")}
            </Btn>
            {invited ? (
              <Btn size="sm" variant="secondary" disabled={!canWrite || busy !== null}
                onClick={() => { setRevokeError(null); setConfirmingRevoke(true); }}>
                {busy === "revoke" ? t("staffRoles.revoking", "Revoking…") : t("staffRoles.revokeInvite", "Revoke invitation")}
              </Btn>
            ) : (
              <Btn size="sm" variant="secondary" disabled={!canWrite || !person.memberId || busy !== null}
                onClick={() => void run("remove", () => removeMemberOp(person.memberId as string), t("staffRoles.accessRemoved", "Workspace access removed"), "access removed").then(onClose)}>
                {t("staffRoles.removeAccess", "Remove workspace access")}
              </Btn>
            )}
          </div>
          <ConfirmDialog open={confirmingRevoke} tone="warning" busy={busy === "revoke"}
            title={formatICU(t("staffRoles.revokeInviteTitle", "Revoke the invitation to {name}?"), { name: person.name })}
            body={t("staffRoles.revokeInviteBody", "The invitation stops working and no workspace access is granted. You can invite them again later.")}
            consequence={person.staffId
              ? t("staffRoles.revokeInviteKeepsRoster", "Their roster record stays as it is. If it was added by mistake too, deactivate it from Staff.")
              : undefined}
            confirmLabel={t("staffRoles.revokeInvite", "Revoke invitation")}
            cancelLabel={t("common.actions.cancel", "Cancel")}
            onCancel={() => { setConfirmingRevoke(false); setRevokeError(null); }}
            onConfirm={() => void revokeInvitation()}>
            {revokeError ? (
              <Banner tone="danger" size="compact">
                {t("staffRoles.couldntRevoke", "Couldn't revoke the invitation — it is still active.")} {revokeError}
              </Banner>
            ) : null}
          </ConfirmDialog>
        </div>
      ) : null}
    </div>
  );
}

/* ── Person page — the nested route a roster row opens ────────────────────
 * `people.person?key=<person.key>&tab=<tab>`. The tab rides the URL so a
 * person's Roles tab is linkable and survives refresh and back/forward.
 * Availability and Absences are DECLARED EMPTY: the operator is specifying
 * them next, and an empty page that says so is honest where invented
 * controls would not be. */
export function PersonPage() {
  const t = useT();
  const nav = useNav();
  const { people, memberOf, staffOf, locations, loading, error, membersUnavailable, reload } = usePeople();
  const canManageWorkspaceRoles = useCanManageWorkspaceRoles(membersUnavailable);
  useDateTimeVersion(); // re-render Last login when the viewer's zone or locale changes
  const key = String(nav.params.key ?? "");
  const raw = String(nav.params.tab ?? "");
  const tab: PersonTab = (PERSON_TABS as string[]).includes(raw) ? (raw as PersonTab) : "personal";
  const person = people.find((p) => p.key === key) ?? null;
  const roles = useMemo(() => rolesFromPeople(people), [people]);
  const lastLogin = person ? lastLoginOf(person, memberOf(person)) : "";
  const home = person?.locationRoles.find((r) => r.home);
  const homeName = home ? (person?.locations.find((l) => l.id === home.locationId)?.name ?? "") : "";
  const roleLabel = person ? (person.roleLabel || roles.find((r) => r.key === person.defaultRoleKey)?.label || "") : "";
  const sectionIcon = (id: PersonTab, size: number): ReactNode =>
    id === "personal" ? <Icon.user size={size} /> : id === "roles" ? <Icon.lock size={size} />
      : id === "availability" ? <Icon.clock size={size} /> : id === "meeting" ? <Icon.loc size={size} /> : <Icon.calendar size={size} />;
  /* The section head over the content (the Service editor's SvcSectionHead):
     icon tile, title, one line on what the section is. */
  const personSections: { id: PersonTab; label: string; icon: ReactNode; desc: string }[] = [
    { id: "personal",     label: t("staffRoles.tabPersonal", "Personal"),         icon: sectionIcon("personal", 16),     desc: "Who this person is, and how their access is set up." },
    { id: "roles",        label: t("staffRoles.roles", "Roles"),                  icon: sectionIcon("roles", 16),        desc: "What this person can do, at each location they work at." },
    { id: "availability", label: t("staffRoles.tabAvailability", "Availability"), icon: sectionIcon("availability", 16), desc: "The weekly hours this person can be booked." },
    { id: "absences",     label: t("staffRoles.tabAbsences", "Absences"),         icon: sectionIcon("absences", 16),     desc: t("staffRoles.absencesBlurb", "Holidays, time off and other absences for this person.") },
    { id: "calendar",     label: t("staffRoles.tabCalendar", "Calendar"),         icon: sectionIcon("calendar", 16),     desc: "The personal calendar that keeps this person's busy time off the booking page." },
    { id: "meeting",      label: t("staffRoles.tabMeetingType", "Meeting type"),  icon: sectionIcon("meeting", 16),      desc: t("staffRoles.meetingBlurb", "How this person meets customers — in person, by phone, or over a video provider.") },
  ];
  const activeSection = personSections.find((s) => s.id === tab) ?? null;
  /* The person's OWN zone, from the roster record — the availability read
     answers the zone the hours are KEPT in, which falls back to the venue's
     when the person has none. Undefined (not null) when there is no roster
     record to ask, so the tabs never claim a source they cannot know. */
  const staffRecord = person ? staffOf(person) : undefined;
  const ownTimezone = staffRecord ? (staffRecord.timezone ?? null) : undefined;
  /* Phone and narrow tablet: the 236px rail would leave the section ~150px,
     one word per line. Below the editor breakpoint the sections become a
     horizontal, scrollable Tabs row ABOVE the content (the Location editor
     stacks its rail at the same width). */
  const compact = useIsMobile(760);
  const tabRow = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Keep the selected section in view when the row scrolls sideways.
    const row = tabRow.current;
    const el = row?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
    if (!row || !el) return;
    const left = el.offsetLeft, right = left + el.offsetWidth;
    if (left < row.scrollLeft) row.scrollLeft = Math.max(0, left - 16);
    else if (right > row.scrollLeft + row.clientWidth) row.scrollLeft = right - row.clientWidth + 16;
  }, [tab, compact]);

  return (
    <Screen rail="people">
      <PageHeader
        title={person?.name ?? t("staffRoles.person", "Person")}
        /* Identity in the subtitle, the way the Service editor carries its status
           pill and facts (operator, 2026-09-30); the separate identity card is gone. */
        subtitle={person ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Avatar size={22} initials={person.init} color={person.color} />
            {statusPillOf(person)}
            {roleLabel ? <StatusPill tone="primary" size="sm">{roleLabel}</StatusPill> : null}
            {homeName ? <StatusPill tone="neutral" size="sm">{homeName}</StatusPill> : null}
            {[person.title, person.email].filter(Boolean).length ? <span>· {[person.title, person.email].filter(Boolean).join(" · ")}</span> : null}
          </span>
        ) : undefined}
        breadcrumb={
          /* ui-raw: breadcrumb link back to the roster — PageHeader takes a node */
          <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("people")}>
            <Icon.arrow dir="left" size={12} /> {t("staffRoles.staff", "Staff")}
          </span>
        }
        /* Send welcome email (CC 0170): only on a saved roster record; the button
           applies the rest of the rules itself. */
        actions={staffRecord && staffRecord.id ? <WelcomeEmailButton member={staffRecord} /> : undefined}
      />


      {/* Left-hand section rail, the same as the Service editor's (operator, 2026-09-30),
          in place of the horizontal tab strip. Same widths, row metrics and
          selected state; the section still rides the URL (?tab=). */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: compact ? "column" : "row", minHeight: 0, minWidth: 0 }}>
      {compact ? (
        /* The row scrolls; the page never does. `max-content` keeps every tab on
           one line and the scroller owns the overflow. */
        <div ref={tabRow} data-testid="person-section-row"
          style={{ position: "relative", flexShrink: 0, overflowX: "auto", overflowY: "hidden", borderBottom: `1px solid ${T.border}`, background: T.canvas, padding: "0 12px" }}>
          <Tabs value={tab} onChange={(id) => nav.replace("people.person", { key, tab: id })}
            aria-label={t("staffRoles.personSections", "Sections")}
            style={{ borderBottom: "none", width: "max-content", minWidth: "100%" }}>
            {personSections.map((s) => (
              <Tab key={s.id} value={s.id} icon={s.icon} testid="person-section" style={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                {s.label}
              </Tab>
            ))}
          </Tabs>
        </div>
      ) : (
      <nav aria-label={t("staffRoles.person", "Person")} style={{ width: 236, flexShrink: 0, borderRight: `1px solid ${T.border}`, padding: "18px 14px", overflow: "auto", background: T.canvas }}>
        {personSections.map((s) => {
          const on = tab === s.id;
          return (
            /* ui-raw: left-hand editor section rail row — EditorSectionNav's pattern (group 8), not Button's */
            <button key={s.id} type="button" aria-current={on ? "page" : undefined} data-testid="person-section" onClick={() => nav.replace("people.person", { key, tab: s.id })} style={{ width: "100%", display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", marginBottom: 4, borderRadius: 9, border: "none", background: on ? T.white : "transparent", color: on ? T.p1 : T.n2, fontWeight: on ? 700 : 500, fontSize: 13.5, cursor: "pointer", fontFamily: T.font, boxShadow: on ? T.shadowSm : "none", textAlign: "left" }}>
              <span style={{ color: on ? T.p1 : T.soft, display: "inline-flex" }}>{s.icon}</span>
              <span style={{ flex: 1 }}>{s.label}</span>
            </button>
          );
        })}
      </nav>
      )}

      <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto", padding: compact ? "16px 16px 48px" : "24px 28px 60px" }}>
        {person && activeSection ? (
          <div style={{ marginBottom: 20 }}>
            <SvcSectionHead icon={sectionIcon(activeSection.id, 18)} title={activeSection.label} desc={activeSection.desc} />
          </div>
        ) : null}
        <PageState noun={t("staffRoles.nounPeople", "people")} icon={<Icon.users size={20} />}
          loading={loading} error={error} onRetry={reload} skeleton={<PersonDetailGhost />}
          empty={!person}
          emptyTitle={t("staffRoles.personNotFound", "This person isn't in the roster")}
          emptyBody={t("staffRoles.personNotFoundBody", "The link may be out of date, or the record was removed. Go back to Staff and pick them from the list.")}
          action={<Btn variant="secondary" onClick={() => nav.go("people")}>{t("staffRoles.backToStaff", "Back to Staff")}</Btn>}>
          {!person ? null : tab === "availability" ? (
            <StaffAvailabilityTab staffId={person.staffId} personName={person.name} ownTimezone={ownTimezone} onOpenDetails={person.staffId && can("identity.team.manage") ? () => nav.go("staff.edit", { id: person.staffId }) : undefined} />
          ) : tab === "absences" ? (
            <StaffAbsencesTab staffId={person.staffId} personName={person.name} ownTimezone={ownTimezone} onOpenDetails={person.staffId && can("identity.team.manage") ? () => nav.go("staff.edit", { id: person.staffId }) : undefined} />
          ) : tab === "calendar" ? (
            <StaffCalendarTab staffId={person.staffId} email={person.email} />
          ) : tab === "meeting" ? (
            <ComingSoonTab icon={<Icon.loc size={16} />}
              title={t("staffRoles.tabMeetingType", "Meeting type")}
              blurb={t("staffRoles.meetingBlurb", "How this person meets customers — in person, by phone, or over a video provider.")}
              note={t("staffRoles.meetingNote", "Per-person meeting providers are separate from the in-person / video / phone methods already set on each availability window. Nothing stores a provider connection for a person yet; both sides are still to be built.")} />
          ) : (
            <PersonRecord person={person} tab={tab} staffRecord={staffOf(person)} locations={locations} roles={roles}
              canManageWorkspaceRoles={canManageWorkspaceRoles} lastLogin={lastLogin} statusPill={statusPillOf}
              onClose={() => nav.go("people")} onChanged={reload} />
          )}
        </PageState>
      </div>
      </div>
    </Screen>
  );
}

/* ── Screen ──────────────────────────────────────────────────────────────── */

export default function StaffAndRoles() {
  const t = useT();
  const nav = useNav();
  const { people, memberOf, locations, loading, error, membersUnavailable, reload } = usePeople();
  const canManageWorkspaceRoles = useCanManageWorkspaceRoles(membersUnavailable);
  useDateTimeVersion(); // re-render Last login when the viewer's zone or locale changes
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const canWrite = can("team.write");

  const apiRoles = useMemo(() => rolesFromPeople(people), [people]);
  const roleLabelOf = (k: string) => apiRoles.find((r) => r.key === k)?.label ?? k;
  /* Capability count per person — the column Settings › Team & roles carried,
     resolved the same way: process assignments first, events as the fallback. */
  const capCountOf = (p: Person) => {
    const a = p.assignments ?? {};
    const primary = a.events ?? Object.values(a).find(Boolean) ?? p.defaultRoleKey;
    if (!primary && !Object.keys(a).length) return "—";
    return effectivePerms({ role: primary, assignments: a }).size || "—";
  };
  /* LOBs come from the tenant's services catalogue. It is not served to this
     screen yet, so the field renders DISABLED naming the op it waits on —
     omitting it silently would hide the gap. */
  const lobs: string[] = [];

  const rows = people.filter((p) => {
    if (q.trim() && !`${p.name} ${p.email} ${p.title}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (loc && !p.locations.some((l) => l.name === loc)) return false;
    return true;
  });
  /* Selection is the shared list hook — it prunes ids that filtering removed,
     which the local Record<string, true> did not: a person selected, then
     filtered out, stayed in the count and in every bulk write. */
  const sel = useRowSelection(rows.map((p) => p.key));
  const selected = rows.filter((p) => sel.selected.has(p.key));
  const filtersActive = !!(q.trim() || loc);

  const statusPill = statusPillOf;

  /* Roster columns for the DS Table (design-system pass, 2026-09-11). The first
     pass hand-rolled <table>/<thead>/<tbody> over local TH/TD style objects, so
     it had none of Table's header/cell rhythm, sticky header, selection column
     or keyboard-reachable rows — clicking a row was a bare <tr onClick>, which
     no keyboard can reach. */
  const columns: TableColumn<Person>[] = [
    { id: "person", header: t("staffRoles.person", "Person"), minWidth: 220, cell: (p) => (
      <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
        <Avatar size={32} initials={p.init} color={p.color} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
          <div style={{ fontSize: 12, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.title || p.email}</div>
        </div>
      </div>
    ) },
    { id: "role", header: t("staffRoles.roleAccess", "Role / access"), minWidth: 190, cell: (p) => (
      <>
        <div style={{ color: T.n1 }}>{p.roleLabel || roleLabelOf(p.defaultRoleKey) || "—"}</div>
        <div style={{ fontSize: 12, color: T.soft, display: "flex", gap: 8 }}>
          {p.signsIn ? <span>{t("staffRoles.signsIn", "Signs in")}</span> : null}
          {p.bookable ? <span>{t("staffRoles.bookable", "Bookable")}</span> : null}
          {!p.signsIn && !p.bookable ? <span>{t("staffRoles.recordOnly", "Record only")}</span> : null}
        </div>
      </>
    ) },
    { id: "location", header: t("staffRoles.location", "Location"), minWidth: 190, cell: (p) => (
      <span style={{ color: T.n2 }}>{p.locations.length ? p.locations.map((l) => l.name).join(", ") : "—"}</span>
    ) },
    { id: "status", header: t("staffRoles.status", "Status"), minWidth: 110, cell: (p) => statusPill(p) },
    { id: "caps", header: t("staffRoles.caps", "Caps"), minWidth: 84, align: "right", cell: (p) => (
      <span style={{ color: T.n2, fontVariantNumeric: "tabular-nums" }}>{capCountOf(p)}</span>
    ) },
    { id: "lastLogin", header: t("staffRoles.lastLogin", "Last login"), minWidth: 180, cell: (p) => (
      <span style={{ color: T.soft, fontSize: 12, lineHeight: 1.4 }}>{lastLoginOf(p, memberOf(p))}</span>
    ) },
  ];

  /* An affordance that cannot do its job renders DISABLED with the reason on
     hover — never an enabled control that fires an apologetic toast
     (CLAUDE.md, no-fake-interactivity). */
  const bulk = (label: string, onClick: () => void): BulkAction => ({ label, onClick });
  const bulkPending = (label: string, op: string): BulkAction => ({
    label, disabled: true, onClick: () => {},
    title: i18nT("staffRoles.pendingOp", "Not served yet — needs ") + op,
  });

  return (
    <Screen rail="people">
      <PageHeader
        title={t("staffRoles.staff", "Staff")}
        subtitle={t("staffRoles.subtitle", "Everyone in the workspace — where they work, what they do there, and what they can access.")}
        actions={canWrite ? <Btn icon={<Icon.plus />} onClick={() => setAdding(true)}>{t("staffRoles.addStaff", "Add staff")}</Btn> : null}
      />

      {/* No tab strip: since the 2026-09-10 split each route carries ONE
          surface, and a strip of one tab is a control that cannot do anything. */}
      <>
          <FilterBar>
            <Input full={false} size="sm" leading={<Icon.search size={14} />} style={{ width: 250 }}
              placeholder={t("staffRoles.searchPeople", "Search name, email or title…")} value={q} onChange={(e) => setQ(e.target.value)} />
            <FilterDropdown label={loc ?? t("staffRoles.anyLocation", "Any location")} active={!!loc}
              options={locations.map((l) => l.name)} onPick={setLoc} onClear={() => setLoc(null)} />
            <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{rows.length} {t("locations.list.of", "of")} {people.length}</span>
          </FilterBar>

          <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "16px 24px 24px" }}>
            {adding ? <AddStaffForm locations={locations} roles={apiRoles} lobs={lobs} canManageWorkspaceRoles={canManageWorkspaceRoles} onClose={() => setAdding(false)} onDone={() => { sel.clear(); reload(); }} /> : null}

            {/* The shared selection toolbar — the screen had hand-rolled a
                near-copy of it, down to two literal lilacs for the border and
                the divider. */}
            <BulkActionBar count={selected.length} noun={t("staffRoles.nounPerson", "person")}
              onClear={sel.clear} style={{ margin: "0 0 12px" }}
              actions={[
                bulkPending(t("staffRoles.assignLocation", "Assign location"), "bulkAssignLocation"),
                bulkPending(t("staffRoles.assignLob", "Assign LOB"), "bulkAssignLob"),
                bulkPending(t("staffRoles.changeRole", "Change role"), "bulkSetRole"),
                bulk(t("staffRoles.reinvite", "Invite / re-invite"), () => {
                  void Promise.all(selected.filter((p) => p.memberId).map((p) => resendInviteOp(p.memberId as string)))
                    .then(() => { toast({ tone: "success", title: t("staffRoles.invitesSent", "Invitations sent"), subtitle: `${selected.length}` }); sel.clear(); })
                    .catch((e) => toast({ tone: "danger", title: t("staffRoles.couldnTInvite", "Couldn't send invitations"), subtitle: e instanceof Error ? e.message : String(e) }));
                }),
                bulkPending(t("staffRoles.deactivate", "Deactivate"), "bulkDeactivate"),
                bulkPending(t("staffRoles.addToGroup", "Add to group"), "bulkAddToGroup"),
                bulkPending(t("common.actions.export", "Export"), "bulkExportPeople"),
              ]} />

            {/* PageState owns the branch order (failed → loading → empty →
                no-match); LoadState + a card EmptyState rendered a failed read
                and an empty roster the same way. */}
            <PageState noun={t("staffRoles.nounPeople", "people")} icon={<Icon.users size={20} />}
              loading={loading} error={error} onRetry={reload}
              empty={rows.length === 0} filtered={filtersActive}
              onClearFilters={() => { setQ(""); setLoc(null); }}
              skeleton="table" skeletonColumns={6}
              emptyTitle={t("staffRoles.nobodyYet", "Nobody on the roster yet")}
              emptyBody={t("staffRoles.nobodyYetBody", "Add the people who work here — where they work, what they do there, and what they can access.")}
              action={canWrite ? <Btn icon={<Icon.plus />} onClick={() => setAdding(true)}>{t("staffRoles.addStaff", "Add staff")}</Btn> : undefined}>
              <Card style={{ padding: 0, overflow: "hidden" }}>
                <Table<Person> columns={columns} rows={rows} getRowId={(p) => p.key}
                  caption={t("staffRoles.staff", "Staff")}
                  stickyHeader
                  selectedIds={sel.selected}
                  onRowSelect={(id) => sel.toggle(id)}
                  onSelectAll={() => sel.toggleAll()}
                  onRowClick={(p) => nav.go("people.person", { key: p.key })}
                  testid="staff-roster-table" />
              </Card>
            </PageState>
          </div>
      </>
    </Screen>
  );
}

/* ── Roles — its own route (2026-09-10, operator) ─────────────────────────
 * Role DEFINITION, split out of the Staff screen's tab strip, served by the
 * identity team API (lib/team-roles-api). The route and rail item are gated on
 * `team.read` (identity.team.read), the capability listing roles declares;
 * creating, editing and deleting custom roles needs `team.write`
 * (identity.team.manage), checked inside RolesTab. Staff keeps the permissions
 * it already had — nothing about the roster's gating changed. */
export function RolesScreen() {
  const t = useT();
  const { roles, loading, error, reload } = useTeamRoles();
  return (
    <Screen rail="roles">
      {/* No PageHeader: RolesTab opens with its own "Manage roles" heading and
          blurb, so a page title above it said the same thing twice. */}
      <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 24px" }}>
        <PageState noun={t("staffRoles.nounRoles", "roles")} icon={<Icon.lock size={20} />}
          loading={loading} error={error} onRetry={reload} skeleton="cards" skeletonRows={4}>
          <RolesTab roles={roles} />
        </PageState>
      </div>
    </Screen>
  );
}
